import React from 'react'

import Avatar from '@mui/material/Avatar'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import { useTheme } from '@mui/material/styles'

import { CategoryIcon, CategoryIconVariants, P, Text } from '@mxenabled/mxui'
import { Icon } from '@mxenabled/mx-icons'
import { CategorySelectorDrawer } from '../../../../../categories'

import {
  ANALYTICS_EVENTS,
  Dialog,
  useCategoryStore,
  useGlobalCopyStore,
} from '../../../../../common'
import type { Transaction } from '../../../../../core'

import { ON_CLICK_EVENTS } from '../../../../constants/OnClickEvents'
import { useTransactionsUiStore } from '../../../../context/hooks'
import { useEvent, useTransactionStore } from '../../../../../common'
import { buildDynamicCopy } from '../../../../../core/utils/Localization'

interface CategoryActionProps {
  transaction: Transaction
}

const CategoryAction: React.FC<CategoryActionProps> = ({ transaction }) => {
  const { addOrUpdateTransactionRule, updateTransaction } = useTransactionStore()
  const { categories } = useCategoryStore()
  const { onEvent } = useEvent()
  const { setShouldDisableDrawerScroll, updateManualTransaction } = useTransactionsUiStore()
  const { transactions: copy } = useGlobalCopyStore()

  const theme = useTheme()

  const [open, setOpen] = React.useState(false)
  const [selectedGuid, setSelectedGuid] = React.useState('')

  const handleCategoryChange = async (applyToAll: boolean) => {
    await updateTransaction({ ...transaction, category_guid: selectedGuid })
    if (applyToAll) {
      await addOrUpdateTransactionRule(selectedGuid, transaction)
    }
    setSelectedGuid('')
    onEvent(ANALYTICS_EVENTS.TRANSACTION_DETAILS_CLICK_CATEGORY, {
      transaction_guid: transaction.guid,
    })
  }

  const handleSelected = (guid: string) => {
    if (transaction.is_manual && !transaction.guid) {
      const category = categories.find((c) => c.guid === guid)
      updateManualTransaction({
        ...transaction,
        category: category?.name,
        category_guid: guid,
        top_level_category_guid: category?.parent_guid || guid,
      })
    } else {
      setSelectedGuid(guid)
    }

    handleCloseDrawer()
  }

  const handleCloseDrawer = () => {
    setOpen(false)
    setShouldDisableDrawerScroll(false)
  }

  const category = React.useMemo(
    () => categories.find((item) => item.guid === selectedGuid),
    [selectedGuid],
  )

  const bgColor = theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100'

  return (
    <ListItem disableGutters={true} disablePadding={true}>
      <ListItemButton
        onClick={() => {
          onEvent(ON_CLICK_EVENTS.ON_TRANSACTION_CATEGORY_CLICK)
          setOpen(true)
        }}
      >
        <ListItemAvatar>
          <Avatar sx={{ bgcolor: bgColor }} variant="rounded">
            <CategoryIcon
              categoryGuid={transaction.top_level_category_guid}
              size={24}
              sx={{ color: 'text.primary' }}
              variant={CategoryIconVariants.Transparent}
            />
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={copy.category_title}
          secondary={
            <Text bold={true} sx={{ color: 'text.primary' }} variant="body1">
              {transaction.category ?? copy.actions_select_a_category}
            </Text>
          }
          slotProps={{ primary: { variant: 'caption', fontSize: 11, fontWeight: 'normal' } }}
        />
        <ListItemIcon>
          <Icon name="chevron_right" sx={{ color: 'text.primary' }} />
        </ListItemIcon>
      </ListItemButton>

      <CategorySelectorDrawer
        initialSelected={transaction.category_guid}
        onClose={handleCloseDrawer}
        onSelect={handleSelected}
        showDrawer={open}
        title={copy.actions_select_a_category}
      />

      <Dialog
        copy={{
          title: buildDynamicCopy(
            copy.actions_apply_to_all_transaction_types,
            transaction.description,
          ) as string,
        }}
        isOpen={Boolean(selectedGuid)}
        onClose={() => setSelectedGuid('')}
        onPrimaryAction={() => handleCategoryChange(true)}
        onSecondaryAction={() => handleCategoryChange(false)}
        primaryText={copy.actions_apply_to_all}
        secondaryText={copy.actions_this_time_only}
      >
        <P variant="subtitle1">
          {
            buildDynamicCopy(
              copy.actions_all_past_and_future,
              transaction.description,
              category?.name,
            ) as string
          }
        </P>
      </Dialog>
    </ListItem>
  )
}

export default CategoryAction
